// src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { ThemeProvider } from './context/ThemeContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { SidebarLayout } from './Sidebar';
import ErrorBoundary from './components/shared/ErrorBoundary';
import LoadingSpinner from './components/shared/LoadingSpinner';
import { ToastViewport } from './components/shared/toast';
import queryClient from './lib/queryClient';

// ── Route-level code splitting (FORM-0018) ───────────────────────────────────
// Every page was in the initial bundle, so a user landing on /login downloaded
// Chart.js, react-select and all 25 pages before seeing a password field. Each
// route is its own chunk now, fetched on navigation.
//
// LoginPage is deliberately NOT split: it is the entry point for anonymous
// visitors, and splitting it only adds a round-trip before the first paint.
import LoginPage from './components/LoginPage';

const DashboardPage = lazy(() => import('./components/DashboardPage'));
const HorseFormPage = lazy(() => import('./components/HorseFormPage'));
const HorseChartPage = lazy(() => import('./components/HorseChartPage'));
const HorseComparePage = lazy(() => import('./components/HorseComparePage'));
const MeetingFormPage = lazy(() => import('./components/MeetingFormPage'));
const RatingsWorkspacePage = lazy(() => import('./components/RatingsWorkspacePage'));
const NominationsPlanPage = lazy(() => import('./components/NominationsPlanPage'));
const HorseFormMobilePage = lazy(() => import('./components/MobileFormPage'));
const HorseIdMapping = lazy(() => import('./settings/horseIdMapping'));
const BlackbookAdmin = lazy(() => import('./settings/BlackbookAdmin'));
const HorseStatsPage = lazy(() => import('./components/HorseStatsPage'));
const SireTablesPage = lazy(() => import('./components/SireTablesPage'));
const SireCropTablesPage = lazy(() => import('./components/SireCropTablesPage'));
const SireUpcomingStartersPage = lazy(() => import('./components/SireUpcomingStartersPage'));
const PedigreePage = lazy(() => import('./components/PedigreePage'));
const NicksPage = lazy(() => import('./components/NicksPage'));
const SireTrendsPage = lazy(() => import('./components/SireTrendsPage'));
const BlackbookPage = lazy(() => import('./components/BlackbookPage'));
const DataPage = lazy(() => import('./components/DataPage'));
const SalesPage = lazy(() => import('./components/SalesPage'));
const RacingPage = lazy(() => import('./components/RacingPage'));
const RacingUpcomingPage = lazy(() => import('./components/RacingUpcomingPage'));
const OhrPredictorPage = lazy(() => import('./components/OhrPredictorPage'));
const SeasonalRecordsPage = lazy(() => import('./components/SeasonalRecordsPage'));
const RaceHistoryPage = lazy(() => import('./components/RaceHistoryPage'));
const TrialMeetingPage = lazy(() => import('./components/TrialMeetingPage'));

// Public routes that should render with the sidebar/app shell when the
// visitor is logged in, but render bare for anonymous visitors. The
// sidebar gates on AuthContext.user — if we always wrapped with
// SidebarLayout, anonymous visitors would see a stuck "Loading..." pane.
const PublicWithOptionalShell = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <SidebarLayout>{children}</SidebarLayout>;
  return <div className="min-h-screen bg-bg text-text">{children}</div>;
};

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DataProvider>
          <Router>
            <BreadcrumbProvider>
            <div className="min-h-screen bg-bg text-text">
              <ErrorBoundary>
                {/* One Suspense boundary around the route table: a chunk is only
                    ever fetched on navigation, so at most one route is pending
                    at a time and per-route boundaries would all look the same. */}
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading…" />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginPage />} />
                    {/* Blackbook is public: renders with the normal app shell
                        (sidebar) when logged in, bare when anonymous. */}
                    <Route path="/blackbook" element={<PublicWithOptionalShell><BlackbookPage /></PublicWithOptionalShell>} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    {/* Protected routes wrapped with Sidebar */}
                    <Route path="/dashboard" element={<ProtectedRoute auth={14}><SidebarLayout><DashboardPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-form" element={<ProtectedRoute auth={14}><SidebarLayout><HorseFormPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-form/:horseId" element={<ProtectedRoute auth={14}><SidebarLayout><HorseFormPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/mobile-form" element={<ProtectedRoute auth={14}><SidebarLayout><HorseFormMobilePage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/mobile-form/:horseId" element={<ProtectedRoute auth={14}><SidebarLayout><HorseFormMobilePage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-chart" element={<ProtectedRoute auth={14}><SidebarLayout><HorseChartPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-compare" element={<ProtectedRoute auth={14}><SidebarLayout><HorseComparePage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/seasonal-records" element={<ProtectedRoute auth={14}><SidebarLayout><SeasonalRecordsPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/meetings/ratings" element={<ProtectedRoute auth={14}><SidebarLayout><RatingsWorkspacePage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/meetings/nominations" element={<ProtectedRoute auth={14}><SidebarLayout><NominationsPlanPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/meetings/:meetingId" element={<ProtectedRoute auth={14}><SidebarLayout><MeetingFormPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-id-map" element={<ProtectedRoute auth={14}><SidebarLayout><HorseIdMapping /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/blackbook-admin" element={<ProtectedRoute auth={5}><SidebarLayout><BlackbookAdmin /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-stats/:entity/:entity_id" element={<ProtectedRoute auth={14}><SidebarLayout><HorseStatsPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/horse-stats" element={<ProtectedRoute auth={14}><SidebarLayout><HorseStatsPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/table/:type" element={<ProtectedRoute auth={14}><SidebarLayout><SireTablesPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/sire-crops" element={<ProtectedRoute auth={14}><SidebarLayout><SireCropTablesPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/sire-upcoming" element={<ProtectedRoute auth={14}><SidebarLayout><SireUpcomingStartersPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/pedigree" element={<ProtectedRoute auth={14}><SidebarLayout><PedigreePage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/nicks" element={<ProtectedRoute auth={14}><SidebarLayout><NicksPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/sire-trends" element={<ProtectedRoute auth={14}><SidebarLayout><SireTrendsPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/data" element={<ProtectedRoute auth={14}><SidebarLayout><DataPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/data/:area" element={<ProtectedRoute auth={14}><SidebarLayout><DataPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/sales" element={<ProtectedRoute auth={14}><SidebarLayout><SalesPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/racing" element={<ProtectedRoute auth={14}><SidebarLayout><RacingPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/racing/race-history" element={<ProtectedRoute auth={14}><SidebarLayout><RaceHistoryPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/racing/upcoming" element={<ProtectedRoute auth={14}><SidebarLayout><RacingUpcomingPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/racing/ohr" element={<ProtectedRoute auth={14}><SidebarLayout><OhrPredictorPage /></SidebarLayout></ProtectedRoute>} />
                    <Route path="/trials" element={<ProtectedRoute auth={14}><Navigate to="/trials/latest?tab=Overview" replace /></ProtectedRoute>} />
                    <Route path="/trials/:meetingId" element={<ProtectedRoute auth={14}><SidebarLayout><TrialMeetingPage /></SidebarLayout></ProtectedRoute>} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              {/* Outside ErrorBoundary: a crashed route must not take the
                  notifications down with it. */}
              <ToastViewport />
            </div>
            </BreadcrumbProvider>
          </Router>
        </DataProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
